/**
 * CLIENT DETAIL PAGE
 * ==================
 * Shows complete client information with:
 * - Client info, current period, employee assignment
 * - Notification schedule (when emails will be sent)
 * - Filing history with reminder tracking
 * - Comments section
 * 
 * NOTIFICATION LOGIC:
 * - PENDING status: Shows future notification dates with countdown
 * - FILED status: Shows "No further notifications (Filing completed)"
 * - INACTIVE status: Shows "No further notifications (Client inactive)"
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Chip,
  Divider,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Menu,
} from '@mui/material';
import {
  ArrowBack,
  Edit,
  CheckCircle,
  MoreVert,
  Business,
  CalendarToday,
  Person,
  Send,
  Schedule,
  Block,
  Info,
  Settings,
  ExpandMore,
  Delete,
} from '@mui/icons-material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import { clientApi, commentApi, filingApi } from '../services/api';
import { Client, FilingRecord, FilingStatus, HSTFrequency, Comment, FrequencyChange } from '../types';
import { format, differenceInDays, addMonths, addYears } from 'date-fns';

const ClientDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [client, setClient] = useState<Client | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingFiling, setEditingFiling] = useState<any>(null);
  const [editFormData, setEditFormData] = useState({
    periodStartDate: '',
    periodEndDate: '',
    dueDate: ''
  });
  const [filings, setFilings] = useState<FilingRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [commentLoading, setCommentLoading] = useState(false);
  const [quickSettingsOpen, setQuickSettingsOpen] = useState(false);
  const [muteNotifications, setMuteNotifications] = useState(false);
  const [comment, setComment] = useState('');
  const [expandedFilings, setExpandedFilings] = useState<Set<string>>(new Set());
  const [filingHistories, setFilingHistories] = useState<Record<string, any[]>>({});
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const canModify = user.role === 'ADMIN' || user.role === 'SUPER_ADMIN';
  const [settingsMenuAnchor, setSettingsMenuAnchor] = useState<null | HTMLElement>(null);
  const settingsMenuOpen = Boolean(settingsMenuAnchor);
  useEffect(() => {
    if (id) {
      fetchClientData();
    }
  }, [id]);
  useEffect(() => {
    if (user.role === 'EMPLOYEE') {
      setSettingsMenuAnchor(null);
      setQuickSettingsOpen(false);
    }
  }, [user.role]);

  const fetchClientData = async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await clientApi.getById(id);
      const clientData = response.data.data;
      
console.log(' DEBUG: Client data received:', clientData);
console.log(' DEBUG: CC Emails:', clientData.ccEmails);
      setClient(clientData);
      const allFilings = clientData.filingRecords || [];
      setFilings(allFilings);
      
      // Find the CURRENT filing period - filing whose DUE DATE is in current month
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const currentMonth = today.getMonth();
      const currentYear = today.getFullYear();

      let currentFiling = allFilings.find((f: FilingRecord) => {
        const dueDate = new Date(f.dueDate);
        dueDate.setHours(0, 0, 0, 0);
        
        const dueDateMonth = dueDate.getMonth();
        const dueDateYear = dueDate.getFullYear();
        
        return (
          (f.status === 'PENDING' || 
          f.status === 'WORK_IN_PROGRESS' || 
          f.status === 'OVERDUE') &&
          dueDateMonth === currentMonth &&
          dueDateYear === currentYear
        );
      });

      // Fallback: if no filing for current month, use nearest upcoming due date
      if (!currentFiling) {
        const sortedFilings = [...allFilings].sort((a, b) => 
          new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime()
        );
        
        currentFiling = sortedFilings.find((f: FilingRecord) => {
          const dueDate = new Date(f.dueDate);
          dueDate.setHours(0, 0, 0, 0);
          
          return (
            (f.status === 'PENDING' || 
            f.status === 'WORK_IN_PROGRESS' || 
            f.status === 'OVERDUE') &&
            dueDate >= today
          );
        });
      }

      // Final fallback: use client's period dates
      if (!currentFiling) {
        currentFiling = allFilings.find((f: FilingRecord) => {
          const filingStart = new Date(f.periodStartDate).toISOString().split('T')[0];
          const clientStart = new Date(clientData.periodStartDate).toISOString().split('T')[0];
          return filingStart === clientStart;
        });
      }
      
      // Store current filing for display
      if (currentFiling) {
        setClient({ ...clientData, currentFilingStatus: currentFiling.status });
      }
      
      // Fetch comments
      try {
        const commentsResponse = await commentApi.getByClient(id);
        setComments(commentsResponse.data.data || []);
      } catch (err) {
        console.error('Error fetching comments:', err);
      }
    } catch (error) {
      console.error('Error fetching client:', error);
      setError('Failed to load client data');
    } finally {
      setLoading(false);
    }
  };
  const fetchFilingHistory = async (filingId: string) => {
    if (filingHistories[filingId]) return; // Already loaded
    
    try {
      const response = await filingApi.getHistory(filingId);
      setFilingHistories(prev => ({
        ...prev,
        [filingId]: response.data.data || []
      }));
    } catch (error) {
      console.error('Error fetching filing history:', error);
    }
  };

  const toggleFilingExpansion = (filingId: string) => {
    const newExpanded = new Set(expandedFilings);
    if (newExpanded.has(filingId)) {
      newExpanded.delete(filingId);
    } else {
      newExpanded.add(filingId);
      fetchFilingHistory(filingId);
    }
    setExpandedFilings(newExpanded);
  };

  const handleOpenQuickSettings = () => {
    if (!client) return;
    setMuteNotifications(client.emailNotificationsMuted || false);
    setQuickSettingsOpen(true);
  };

  const handleCloseQuickSettings = () => {
    setQuickSettingsOpen(false);
    setMuteNotifications(false);
  };

  const handleSaveQuickSettings = async () => {
    if (!client || !id) return;

    try {
      await clientApi.update(id, {
        emailNotificationsMuted: muteNotifications,
      });
      handleCloseQuickSettings();
      await fetchClientData();
    } catch (error) {
      console.error('Error updating client:', error);
      alert('Failed to mute notifications');
    }
  };

  const handleSaveComment = async () => {
    if (!comment.trim() || !id) return;
    
    try {
      setCommentLoading(true);
      await commentApi.create({
        clientId: id,
        content: comment.trim(),
      });
      setComment('');
      // Refresh comments
      const commentsResponse = await commentApi.getByClient(id);
      setComments(commentsResponse.data.data || []);
    } catch (error) {
      console.error('Error saving comment:', error);
      alert('Failed to save comment');
    } finally {
      setCommentLoading(false);
    }
  };
const handleEditFiling = (filing: any) => {
    console.log('📝 Opening edit dialog for filing:', filing);
    
    const formatDate = (date: string) => {
      return new Date(date).toISOString().split('T')[0];
    };

    setEditingFiling(filing);
    setEditFormData({
      periodStartDate: formatDate(filing.periodStartDate),
      periodEndDate: formatDate(filing.periodEndDate),
      dueDate: formatDate(filing.dueDate)
    });
    setEditDialogOpen(true);
  };

  const handleSaveEditedFiling = async () => {
    if (!editingFiling) return;

    try {
      console.log('💾 Saving edited filing:', editFormData);

      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/clients/${client?.id}/filings/${editingFiling.id}`,
        {
          method: 'PUT',
	headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
          body: JSON.stringify(editFormData)
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update filing');
      }

      console.log('✅ Filing updated successfully');
      
      setEditDialogOpen(false);
      setEditingFiling(null);
      
      fetchClientData();

      alert('Filing dates updated successfully!');

    } catch (error: any) {
      console.error('❌ Error updating filing:', error);
      alert(`Failed to update filing: ${error.message}`);
    }
  };
  const getStatusColor = (status: FilingStatus) => {
    switch (status) {
      case FilingStatus.FILED:
        return { bg: '#D1FAE5', text: '#065F46', border: '#6EE7B7' };
      case FilingStatus.PENDING:
        return { bg: '#FEF3C7', text: '#92400E', border: '#FCD34D' };
      case FilingStatus.WORK_IN_PROGRESS:
        return { bg: '#DBEAFE', text: '#1E40AF', border: '#93C5FD' };
      case FilingStatus.RECEIVED:
        return { bg: '#E0E7FF', text: '#3730A3', border: '#A5B4FC' };
      case FilingStatus.OVERDUE:
        return { bg: '#FEE2E2', text: '#991B1B', border: '#FCA5A5' };
      case FilingStatus.INACTIVE:
        return { bg: '#F3F4F6', text: '#374151', border: '#D1D5DB' };
      default:
        return { bg: '#F3F4F6', text: '#374151', border: '#D1D5DB' };
    }
  };

    const handleDelete = async () => {
    if (!id || !client) return;
    
    const confirmMessage = `Are you sure you want to delete ${client.clientName}? This will permanently delete all filing records and cannot be undone.`;
    
    if (window.confirm(confirmMessage)) {
      try {
        await clientApi.delete(id);
        navigate('/clients');
      } catch (error) {
        console.error('Error deleting client:', error);
        alert('Failed to delete client');
      }
    }
  };

  const getNotificationSchedule = (periodEnd: Date, dueDate: Date, frequency: HSTFrequency) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Normalize to start of day
    
    const schedule = [];

    if (frequency === HSTFrequency.MONTHLY || frequency === HSTFrequency.QUARTERLY) {
      // Schedule for Monthly/Quarterly: 5 notifications
      
      // 1. One month before period end
      const oneMonthBefore = new Date(periodEnd);
      oneMonthBefore.setMonth(oneMonthBefore.getMonth() - 1);
      schedule.push({
        name: '1 Month Before Period End',
        date: oneMonthBefore,
        daysFromNow: differenceInDays(oneMonthBefore, today),
      });

      // 2. One day after period end
      const oneDayAfter = new Date(periodEnd);
      oneDayAfter.setDate(oneDayAfter.getDate() + 1);
      schedule.push({
        name: '1 Day After Period End',
        date: oneDayAfter,
        daysFromNow: differenceInDays(oneDayAfter, today),
      });

      // 3. Two weeks after period end
      const twoWeeksAfter = new Date(periodEnd);
      twoWeeksAfter.setDate(twoWeeksAfter.getDate() + 14);
      schedule.push({
        name: '2 Weeks After Period End',
        date: twoWeeksAfter,
        daysFromNow: differenceInDays(twoWeeksAfter, today),
      });

      // 4. Two weeks before due date
      const twoWeeksBefore = new Date(dueDate);
      twoWeeksBefore.setDate(twoWeeksBefore.getDate() - 14);
      schedule.push({
        name: '2 Weeks Before Due Date',
        date: twoWeeksBefore,
        daysFromNow: differenceInDays(twoWeeksBefore, today),
      });

      // 5. One week before due date
      const oneWeekBefore = new Date(dueDate);
      oneWeekBefore.setDate(oneWeekBefore.getDate() - 7);
      schedule.push({
        name: '1 Week Before Due Date',
        date: oneWeekBefore,
        daysFromNow: differenceInDays(oneWeekBefore, today),
      });
    } else if (frequency === HSTFrequency.ANNUAL) {
      // Schedule for Annual: 4 notifications
      
      // 1. One month before period end
      const oneMonthBefore = new Date(periodEnd);
      oneMonthBefore.setMonth(oneMonthBefore.getMonth() - 1);
      schedule.push({
        name: '1 Month Before Period End',
        date: oneMonthBefore,
        daysFromNow: differenceInDays(oneMonthBefore, today),
      });

      // 2. One month before due date
      const oneMonthBeforeDue = new Date(dueDate);
      oneMonthBeforeDue.setMonth(oneMonthBeforeDue.getMonth() - 1);
      schedule.push({
        name: '1 Month Before Due Date',
        date: oneMonthBeforeDue,
        daysFromNow: differenceInDays(oneMonthBeforeDue, today),
      });

      // 3. Two weeks before due date
      const twoWeeksBefore = new Date(dueDate);
      twoWeeksBefore.setDate(twoWeeksBefore.getDate() - 14);
      schedule.push({
        name: '2 Weeks Before Due Date',
        date: twoWeeksBefore,
        daysFromNow: differenceInDays(twoWeeksBefore, today),
      });

      // 4. One week before due date
      const oneWeekBefore = new Date(dueDate);
      oneWeekBefore.setDate(oneWeekBefore.getDate() - 7);
      schedule.push({
        name: '1 Week Before Due Date',
        date: oneWeekBefore,
        daysFromNow: differenceInDays(oneWeekBefore, today),
      });
    }

    return schedule.sort((a, b) => a.date.getTime() - b.date.getTime());
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress sx={{ color: '#667eea' }} />
      </Box>
    );
  }

  if (error || !client) {
    return (
      <Box sx={{ pb: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error || 'Client not found'}
        </Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/clients')}>
          Back to Clients
        </Button>
      </Box>
    );
  }

const statusColors = getStatusColor(client.status);

// Find the CURRENT filing (due date in current month)
const today = new Date();
today.setHours(0, 0, 0, 0);
const currentMonth = today.getMonth();
const currentYear = today.getFullYear();

const currentFilingForDisplay = filings.find((f: FilingRecord) => {
  const dueDate = new Date(f.dueDate);
  dueDate.setHours(0, 0, 0, 0);
  const dueDateMonth = dueDate.getMonth();
  const dueDateYear = dueDate.getFullYear();
  
  return (
    (f.status === FilingStatus.PENDING || 
    f.status === FilingStatus.WORK_IN_PROGRESS ||
    f.status === FilingStatus.OVERDUE) &&
    dueDateMonth === currentMonth &&
    dueDateYear === currentYear
  );
}) || filings.find((f: FilingRecord) => {
  // Fallback: nearest upcoming filing
  const dueDate = new Date(f.dueDate);
  dueDate.setHours(0, 0, 0, 0);
  
  return (
    (f.status === FilingStatus.PENDING || 
    f.status === FilingStatus.WORK_IN_PROGRESS ||
    f.status === FilingStatus.OVERDUE) &&
    dueDate >= today
  );
});

// Get notification schedule for ALL upcoming filings (not just current)
const getAllUpcomingNotifications = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  // Get all pending/in-progress filings sorted by period end date
  const upcomingFilings = filings
    .filter((f: FilingRecord) => 
      f.status === FilingStatus.PENDING &&
      new Date(f.dueDate) >= today
    )
    .sort((a, b) => 
      new Date(a.periodEndDate).getTime() - new Date(b.periodEndDate).getTime()
    )
    .slice(0, 2); // Show next 2 filings to avoid clutter

  const allNotifications: any[] = [];

  upcomingFilings.forEach((filing) => {
    const schedule = getNotificationSchedule(
      new Date(filing.periodEndDate),
      new Date(filing.dueDate),
      client.hstFrequency
    );

    // Add period info to each notification
    schedule.forEach((notification) => {
      allNotifications.push({
        ...notification,
        filingPeriod: `${new Date(filing.periodStartDate).toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric',
          timeZone: 'UTC' 
        })} - ${new Date(filing.periodEndDate).toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric', 
          year: 'numeric',
          timeZone: 'UTC' 
        })}`,
        filingDueDate: new Date(filing.dueDate).toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric', 
          year: 'numeric',
          timeZone: 'UTC' 
        }),
      });
    });
  });

  // Sort all notifications by date
  return allNotifications.sort((a, b) => a.date.getTime() - b.date.getTime());
};

const notificationSchedule = getAllUpcomingNotifications();

// Find the actual current filing (due date in current month or nearest upcoming)
// Find the actual current filing - PRIORITIZE PENDING status
// First, try to find a PENDING filing in current month or upcoming
const actualCurrentFiling = filings.find((f: FilingRecord) => {
  const dueDate = new Date(f.dueDate);
  dueDate.setHours(0, 0, 0, 0);
  const dueDateMonth = dueDate.getMonth();
  const dueDateYear = dueDate.getFullYear();
  
  return (
    f.status === FilingStatus.PENDING &&
    dueDateMonth === currentMonth &&
    dueDateYear === currentYear
  );
}) || filings.find((f: FilingRecord) => {
  // Fallback: nearest upcoming PENDING filing
  const dueDate = new Date(f.dueDate);
  dueDate.setHours(0, 0, 0, 0);
  
  return (
    f.status === FilingStatus.PENDING &&
    dueDate >= today
  );
}) || filings.find((f: FilingRecord) => {
  // Fallback: any active filing in current month
  const dueDate = new Date(f.dueDate);
  dueDate.setHours(0, 0, 0, 0);
  const dueDateMonth = dueDate.getMonth();
  const dueDateYear = dueDate.getFullYear();
  
  return (
    (f.status === FilingStatus.WORK_IN_PROGRESS ||
    f.status === FilingStatus.OVERDUE ||
    f.status === FilingStatus.RECEIVED) &&
    dueDateMonth === currentMonth &&
    dueDateYear === currentYear
  );
}) || filings.find((f: FilingRecord) => {
  // Final fallback: nearest upcoming active filing
  const dueDate = new Date(f.dueDate);
  dueDate.setHours(0, 0, 0, 0);
  
  return (
    (f.status === FilingStatus.WORK_IN_PROGRESS ||
    f.status === FilingStatus.OVERDUE ||
    f.status === FilingStatus.RECEIVED) &&
    dueDate >= today
  );
});

// Determine current filing status
let currentFilingStatus;
if (client.status === FilingStatus.INACTIVE) {
  currentFilingStatus = FilingStatus.INACTIVE;
} else if (actualCurrentFiling) {
  currentFilingStatus = actualCurrentFiling.status;
} else {
  currentFilingStatus = client.status;
}
const notificationsActive = currentFilingStatus === FilingStatus.PENDING &&
                            !client.emailNotificationsMuted;

let notificationMessage = '';
  if (client.emailNotificationsMuted) {
    notificationMessage = 'Email notifications are muted for this client';
  } else if (currentFilingStatus === FilingStatus.WORK_IN_PROGRESS) {
    notificationMessage = 'No further notifications (Work in progress - notifications paused)';
  } else if (currentFilingStatus === FilingStatus.RECEIVED) {
    notificationMessage = 'No further notifications (Documents received - awaiting filing)';
  } else if (currentFilingStatus === FilingStatus.FILED) {
    notificationMessage = 'No further notifications (Filing completed)';
  } else if (client.status === FilingStatus.INACTIVE) {
    notificationMessage = 'No further notifications (Client inactive - service paused)';
  }

  return (
    <Box sx={{ backgroundColor: '#F8F9FA', p: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 10}}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/clients')}
          sx={{ 
            mb: 2,
            textTransform: 'none',
            color: '#667eea',
            fontWeight: 600,
            '&:hover': { backgroundColor: 'rgba(102, 126, 234, 0.08)' }
          }}
        >
          Back to Clients
        </Button>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 2, color: '#1a1a1a' }}>
              {client.clientName}
            </Typography>
            <Typography variant="body2" sx={{ color: '#666666', mb: 2 }}>
              Client Profile & Filing History
            </Typography>
          </Box>
          <Box>
          {canModify && (
            <Button
              startIcon={<Settings />}
              onClick={(e) => setSettingsMenuAnchor(e.currentTarget)}
              sx={{
                textTransform: 'none',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: '#FFFFFF',
                fontWeight: 600,
                px: 3,
                py: 1.25,
                '&:hover': { background: 'linear-gradient(135deg, #5568d3 0%, #63408a 100%)' },
              }}
            >
              Settings
            </Button>
          )}

          {canModify && (
            <Menu
              anchorEl={settingsMenuAnchor}
              open={settingsMenuOpen}
              onClose={() => setSettingsMenuAnchor(null)}
              PaperProps={{
                sx: {
                  mt: 1,
                  minWidth: 200,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                }
              }}
            >

            {canModify && (
              <MenuItem
                onClick={() => {
                  setSettingsMenuAnchor(null);
                  handleOpenQuickSettings();
                }}
                sx={{ py: 1.5, px: 2 }}
              >
                <Block sx={{ mr: 2, fontSize: 20 }} />
                Mute Emails
              </MenuItem>
            )}

              
              {canModify && (
                <MenuItem
                  onClick={() => {
                    setSettingsMenuAnchor(null);
                    navigate(`/clients/edit/${id}`);
                  }}
                  sx={{ py: 1.5, px: 2 }}
                >
                  <Edit sx={{ mr: 2, fontSize: 20 }} />
                  Edit Client
                </MenuItem>
              )}
              
              {canModify && (
                <MenuItem
                  onClick={async () => {
                    setSettingsMenuAnchor(null);
                    if (!id) return;
                    const newStatus = client.status === FilingStatus.INACTIVE ? FilingStatus.PENDING : FilingStatus.INACTIVE;
                    const action = newStatus === FilingStatus.INACTIVE ? 'mark as inactive' : 'reactivate';
                    
                    if (window.confirm(`Are you sure you want to ${action} this client?`)) {
                      try {
                        await clientApi.update(id, { status: newStatus });
                        await fetchClientData();
                      } catch (error) {
                        console.error('Error updating client status:', error);
                        alert('Failed to update client status');
                      }
                    }
                  }}
                  sx={{ 
                    py: 1.5, 
                    px: 2,
                    color: client.status === FilingStatus.INACTIVE ? '#10B981' : '#F59E0B'
                  }}
                >
                  <Block sx={{ mr: 2, fontSize: 20 }} />
                  {client.status === FilingStatus.INACTIVE ? 'Reactivate Client' : 'Mark as Inactive'}
                </MenuItem>
              )}
              
              {canModify && (
                <MenuItem
                  onClick={() => {
                    setSettingsMenuAnchor(null);
                    handleDelete();
                  }}
                  sx={{ py: 1.5, px: 2, color: '#EF4444' }}
                >
                  <Delete sx={{ mr: 2, fontSize: 20 }} />
                  Delete Client
                </MenuItem>
              )}
            </Menu>
          )}
          </Box>
          </Box>

      {/* Cards Grid */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {/* Row 1: Client Info + Current Period + Employee */}
            <Box sx={{ 
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: 5
            }}>
            {/* Client Information */}
            <Card sx={{ 
              borderRadius: 2,
            border: '1px solid #E5E7EB',
            m : 1,
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              boxShadow: '0 4px 12px rgba(102, 126, 234, 0.15)',
              transform: 'translateY(-2px)',
            }
          }} elevation={0}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.125rem' }}>Client Info</Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="caption" sx={{ color: '#6B7280', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Name
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>{client.clientName}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: '#6B7280', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Email
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>{client.clientEmail}</Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Frequency Row */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography
                    variant="caption"
                    sx={{
                        width: 80, 
                        color: '#6B7280',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                    }}
                    >
                    Frequency
                    </Typography>

                    <Chip
                    label={client.hstFrequency}
                    size="small"
                    sx={{ fontWeight: 600 }}
                    />
                </Box>

                {/* Client Status Row */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      width: 80, 
                      color: '#6B7280',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}
                  >
                    Client Status
                  </Typography>

                  <Chip
                    label={client.status === FilingStatus.INACTIVE ? 'INACTIVE' : 'ACTIVE'}
                    size="small"
                    sx={{
                      backgroundColor: client.status === FilingStatus.INACTIVE ? '#F3F4F6' : '#D1FAE5',
                      color: client.status === FilingStatus.INACTIVE ? '#6B7280' : '#065F46',
                      border: `1px solid ${client.status === FilingStatus.INACTIVE ? '#D1D5DB' : '#6EE7B7'}`,
                      fontWeight: 600
                    }}
                  />
                </Box>
                </Box>
                </Box>
            </CardContent>
          </Card>

          {/* Current Period */}
          <Card sx={{ 
            borderRadius: 2,
            border: '1px solid #E5E7EB',
            m: 1,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              boxShadow: '0 4px 12px rgba(102, 126, 234, 0.15)',
              transform: 'translateY(-2px)',
            }
          }} elevation={0}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.125rem' }}>Current Period</Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="caption" sx={{ color: '#6B7280', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Period Start
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>
                {new Date(currentFilingForDisplay?.periodStartDate || client.periodStartDate).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric', timeZone: 'UTC' })}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: '#6B7280', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Period End
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>
                {new Date(currentFilingForDisplay?.periodEndDate || client.periodEndDate).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric', timeZone: 'UTC' })}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: '#6B7280', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Filing Due
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5, color: '#EF4444' }}>
                {new Date(currentFilingForDisplay?.dueDate || client.dueDate).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric', timeZone: 'UTC' })}
                </Typography>
              </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: '#6B7280', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Filing Status
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <Chip
                      label={currentFilingStatus}
                      size="small"
                      sx={{
                        backgroundColor: getStatusColor(currentFilingStatus).bg,
                        color: getStatusColor(currentFilingStatus).text,
                        border: `1px solid ${getStatusColor(currentFilingStatus).border}`,
                        fontWeight: 600,
                        fontSize: '0.75rem'
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Employee */}
          <Card sx={{ 
            borderRadius: 2,
            border: '1px solid #E5E7EB',
            m : 1,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              boxShadow: '0 4px 12px rgba(102, 126, 234, 0.15)',
              transform: 'translateY(-2px)',
            }
          }} elevation={0}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.125rem' }}>Employee</Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="caption" sx={{ color: '#6B7280', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Assigned To
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>{client.employeeAssigned}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: '#6B7280', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Email
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>{client.employeeEmail}</Typography>
                </Box>
                {client.ccEmails && client.ccEmails.length > 0 && (
                  <Box>
                    <Typography variant="caption" sx={{ color: '#6B7280', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1, display: 'block' }}>
                      CC Recipients
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {client.ccEmails.map((email, index) => (
                        <Chip key={index} label={email} size="small" sx={{ fontSize: '0.7rem' }} />
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Box>
        </Box>

        {/* Email Notifications Muted Alert */}
        {client.emailNotificationsMuted && (
          <Alert severity="warning" sx={{ 
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
            my: 4,
          }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Email Notifications: MUTED
            </Typography>
          </Alert>
        )}
        {/* Notification Schedule - Collapsible */}
          <Accordion 
            defaultExpanded={false}
            sx={{ 
              border: '1px solid #E5E7EB',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
              my: 4,
            '&:before': { display: 'none' },
            borderRadius: '8px !important',
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMore />}
            sx={{
              background: notificationsActive 
                ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)'
                : 'linear-gradient(135deg, rgba(107, 114, 128, 0.05) 0%, rgba(156, 163, 175, 0.05) 100%)',
              '& .MuiAccordionSummary-content': { my: 2 },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {notificationsActive ? (
                <Schedule sx={{ color: '#667eea', fontSize: 24 }} />
              ) : (
                <Block sx={{ color: '#6B7280', fontSize: 24 }} />
              )}
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.25rem' }}>
                Email Notification Schedule
              </Typography>
            </Box>
          </AccordionSummary>
          
          <AccordionDetails sx={{ pt: 0 }}>
            {!notificationsActive && (
              <Alert 
                severity="info" 
                icon={<Info />}
                sx={{ mb: 3 }}
              >
                {notificationMessage}
              </Alert>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {notificationSchedule.map((notification, index) => {
                const isPast = notification.daysFromNow < 0;
                
                return (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      p: 2.5,
                      backgroundColor: notificationsActive ? '#FFFFFF' : '#F9FAFB',
                      borderRadius: 2,
                      border: '1px solid #E5E7EB',
                      opacity: notificationsActive ? 1 : 0.6,
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {notification.name}
                        </Typography>
                        {notification.filingPeriod && (
                          <Chip
                            label={`Period: ${notification.filingPeriod}`}
                            size="small"
                            sx={{
                              backgroundColor: '#F3F4F6',
                              color: '#374151',
                              fontSize: '0.7rem',
                              height: 20,
                              fontWeight: 600,
                            }}
                          />
                        )}
                      </Box>
                      <Typography variant="caption" sx={{ color: '#6B7280', display: 'block' }}>
                        {client.emailNotificationsMuted 
                          ? 'Cancelled (Notifications Muted)' 
                          : notification.date.toLocaleDateString('en-US', { month: 'long', day: '2-digit', year: 'numeric', timeZone: 'UTC' })
                        }
                      </Typography>
                      {notification.filingDueDate && (
                        <Typography variant="caption" sx={{ color: '#9CA3AF', fontSize: '0.7rem', display: 'block', mt: 0.5 }}>
                          Filing Due: {notification.filingDueDate}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      {notificationsActive ? (
                        isPast ? (
                          <Chip 
                            label="Passed" 
                            size="small" 
                            sx={{ 
                              backgroundColor: '#F3F4F6',
                              color: '#6B7280',
                              fontWeight: 600
                            }} 
                          />
                        ) : (
                          <Chip 
                            label={`In ${notification.daysFromNow} days`} 
                            size="small" 
                            sx={{ 
                              backgroundColor: notification.daysFromNow <= 7 ? '#FEF3C7' : '#DBEAFE',
                              color: notification.daysFromNow <= 7 ? '#92400E' : '#1E40AF',
                              fontWeight: 600,
                              border: `1px solid ${notification.daysFromNow <= 7 ? '#FCD34D' : '#93C5FD'}`,
                            }} 
                          />
                        )
                      ) : (
                        <Chip 
                          label={client.emailNotificationsMuted ? "Cancelled" : "Inactive"}
                          size="small" 
                          sx={{ 
                            backgroundColor: client.emailNotificationsMuted ? '#FEE2E2' : '#F3F4F6',
                            color: client.emailNotificationsMuted ? '#991B1B' : '#6B7280',
                            fontWeight: 600
                          }} 
                        />
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* Comments Section */}
          <Paper sx={{ 
            p: 4, 
            border: '1px solid #E5E7EB',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
            my: 4,
          }} elevation={0}>
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.25rem', mb: 3 }}>
            Comments ({comments.length})
          </Typography>
          
          {/* Existing Comments */}
          {comments.length > 0 && (
            <Box sx={{ mb: 3, maxHeight: 400, overflowY: 'auto' }}>
              {comments.map((c) => (
                <Box
                  key={c.id}
                  sx={{
                    mb: 2,
                    p: 2,
                    backgroundColor: '#F9FAFB',
                    borderRadius: 1,
                    border: '1px solid #E5E7EB',
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151' }}>
                        {c.userName}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#6B7280' }}>
                        {new Date(c.createdAt).toLocaleString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="body2" sx={{ color: '#374151', whiteSpace: 'pre-wrap' }}>
                    {c.content}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
          
          {/* Add New Comment */}
          <Divider sx={{ mb: 2 }} />
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1.5, color: '#374151' }}>
            Add New Comment
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="Add notes or comments about this client..."
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#F9FAFB',
                '& fieldset': { borderColor: '#E5E7EB' },
                '&:hover fieldset': { borderColor: '#667eea' },
                '&.Mui-focused fieldset': { borderColor: '#667eea', borderWidth: '2px' },
              },
            }}
          />
          
          <Button
            startIcon={commentLoading ? <CircularProgress size={16} /> : <Send />}
            disabled={!comment.trim() || commentLoading}
            onClick={handleSaveComment}
            sx={{
              textTransform: 'none',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: '#FFFFFF',
              fontWeight: 600,
              px: 4,
              py: 1.25,
              '&:hover': { background: 'linear-gradient(135deg, #5568d3 0%, #63408a 100%)' },
              '&:disabled': { background: '#E5E7EB', color: '#9CA3AF' },
            }}
          >
            {commentLoading ? 'Saving...' : 'Save Comment'}
          </Button>
        </Paper>

        {/* Filing History */}
          <Paper sx={{ 
            p: 4, 
            border: '1px solid #E5E7EB',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
            my: 4,
          }} elevation={0}>
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.25rem', mb: 3 }}>
            Filing History
          </Typography>
          
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#F9FAFB' }}>
                  {canModify && <TableCell sx={{ fontWeight: 700, color: '#374151' }}>Actions</TableCell>}
                  <TableCell sx={{ fontWeight: 700, color: '#374151' }}>Period</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#374151' }}>Due Date</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#374151' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#374151' }}>Reminders Sent</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#374151' }}>Notes</TableCell>
                  {canModify && <TableCell sx={{ fontWeight: 700, color: '#374151' }}>Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {filings.map((filing) => {
                  const filingColors = getStatusColor(filing.status);
                  const remindersSent = [];
                  
                  if (filing.reminderOneMonthSent) remindersSent.push('1 Month');
                  if (filing.reminderTwoWeeksSent) remindersSent.push('2 Weeks');
                  if (filing.reminderOneWeekSent) remindersSent.push('1 Week');
                  if (filing.reminderOneDayAfterPeriodEnd) remindersSent.push('1 Day After');
                  if (filing.reminderTwoWeeksAfterPeriodEnd) remindersSent.push('2 Weeks After');
                  
                  const isExpanded = expandedFilings.has(filing.id);
                  const history = filingHistories[filing.id] || [];
                  
                  return (
                    <React.Fragment key={filing.id}>
                    <TableRow 
                      sx={{ 
                        '&:hover': { backgroundColor: '#F9FAFB', cursor: 'pointer' },
                        transition: 'background-color 0.2s'
                      }}
                      onClick={(e) => {
                        const target = e.target as HTMLElement;
                        if (!target.closest('button') && !target.closest('.actions-cell')) {
                          navigate(`/filings?filingId=${filing.id}`);
                        }
                      }}
                    >
                      {canModify && (
                        <TableCell className="actions-cell" onClick={(e) => e.stopPropagation()}>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<EditIcon />}
                            onClick={() => handleEditFiling(filing)}
                            sx={{ 
                              minWidth: 80,
                              textTransform: 'none',
                              borderColor: '#667eea',
                              color: '#667eea',
                              '&:hover': {
                                borderColor: '#5568d3',
                                backgroundColor: 'rgba(102, 126, 234, 0.08)',
                              }
                            }}
                          >
                            Edit
                          </Button>
                        </TableCell>
                      )}
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleFilingExpansion(filing.id);
                            }}
                            sx={{ 
                              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                              transition: 'transform 0.2s'
                            }}
                          >
                            <ExpandMore fontSize="small" />
                          </IconButton>
                          <Typography variant="body2" sx={{ color: '#667eea', fontWeight: 600 }}>
                            {new Date(filing.periodStartDate).toLocaleDateString('en-US', { month: 'short', day: '2-digit', timeZone: 'UTC' })} - {new Date(filing.periodEndDate).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric', timeZone: 'UTC' })}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {new Date(filing.dueDate).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric', timeZone: 'UTC' })}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={client.status === FilingStatus.INACTIVE ? 'INACTIVE' : filing.status}
                          size="small"
                          sx={{
                            backgroundColor: client.status === FilingStatus.INACTIVE 
                              ? getStatusColor(FilingStatus.INACTIVE).bg 
                              : filingColors.bg,
                            color: client.status === FilingStatus.INACTIVE 
                              ? getStatusColor(FilingStatus.INACTIVE).text 
                              : filingColors.text,
                            border: `1px solid ${client.status === FilingStatus.INACTIVE 
                              ? getStatusColor(FilingStatus.INACTIVE).border 
                              : filingColors.border}`,
                            fontWeight: 600,
                            fontSize: '0.75rem',
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        {remindersSent.length > 0 ? (
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {remindersSent.map((reminder, idx) => (
                              <Chip 
                                key={idx} 
                                label={reminder} 
                                size="small" 
                                icon={<CheckCircle />} 
                                sx={{
                                  backgroundColor: '#D1FAE5',
                                  color: '#065F46',
                                  fontSize: '0.7rem',
                                  fontWeight: 600,
                                  '& .MuiChip-icon': { color: '#10B981' }
                                }}
                              />
                            ))}
                          </Box>
                        ) : (
                          <Typography variant="caption" sx={{ color: '#6B7280' }}>
                            None yet
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: '#6B7280', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {filing.notes || '-'}
                        </Typography>
                      </TableCell>
                      {canModify && (
                        <TableCell className="actions-cell">
                          {canModify && filing.status !== FilingStatus.FILED && filing.status !== FilingStatus.RECEIVED ? (
                            <IconButton
                              size="small"
                              onClick={async () => {
                                if (window.confirm('Delete this filing record? This cannot be undone.')) {
                                  try {
                                    await filingApi.delete(filing.id);
                                    await fetchClientData();
                                  } catch (error) {
                                    console.error('Error deleting filing:', error);
                                    alert('Failed to delete filing');
                                  }
                                }
                              }}
                              sx={{ color: '#EF4444' }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          ) : (
                            <Typography variant="caption" sx={{ color: '#9CA3AF', fontStyle: 'italic' }}>
                              Cannot delete
                            </Typography>
                          )}
                        </TableCell>
                      )}
                      </TableRow>
                              
                              {/* Status History Expansion */}
                              {isExpanded && (
                                <TableRow>
                                  <TableCell colSpan={canModify ? 7 : 6} sx={{ py: 0, backgroundColor: '#F9FAFB' }}>
                                    <Box sx={{ py: 2, px: 3 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: '#374151' }}>
                                      Status Change History
                                    </Typography>

                                    {/* Email Notifications */}
                                    <Box sx={{ mb: 3, p: 2, backgroundColor: '#F0F9FF', borderRadius: 1, border: '1px solid #BAE6FD' }}>
                                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: '#374151', fontSize: '0.875rem' }}>
                                        Email Notifications
                                      </Typography>
                                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                        {filing.reminderOneMonthSent && (
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <CheckCircle sx={{ fontSize: 16, color: '#10B981' }} />
                                            <Typography variant="caption" sx={{ color: '#374151' }}>
                                              1 Month Before Period End - Sent
                                            </Typography>
                                          </Box>
                                        )}
                                        {filing.reminderTwoWeeksSent && (
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <CheckCircle sx={{ fontSize: 16, color: '#10B981' }} />
                                            <Typography variant="caption" sx={{ color: '#374151' }}>
                                              2 Weeks Before Period End - Sent
                                            </Typography>
                                          </Box>
                                        )}
                                        {filing.reminderOneWeekSent && (
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <CheckCircle sx={{ fontSize: 16, color: '#10B981' }} />
                                            <Typography variant="caption" sx={{ color: '#374151' }}>
                                              1 Week Before Period End - Sent
                                            </Typography>
                                          </Box>
                                        )}
                                        {filing.reminderOneDayAfterPeriodEnd && (
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <CheckCircle sx={{ fontSize: 16, color: '#10B981' }} />
                                            <Typography variant="caption" sx={{ color: '#374151' }}>
                                              1 Day After Period End - Sent
                                            </Typography>
                                          </Box>
                                        )}
                                        {filing.reminderTwoWeeksAfterPeriodEnd && (
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <CheckCircle sx={{ fontSize: 16, color: '#10B981' }} />
                                            <Typography variant="caption" sx={{ color: '#374151' }}>
                                              2 Weeks After Period End - Sent
                                            </Typography>
                                          </Box>
                                        )}
                                        {filing.reminderOneMonthBeforeDue && (
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <CheckCircle sx={{ fontSize: 16, color: '#10B981' }} />
                                            <Typography variant="caption" sx={{ color: '#374151' }}>
                                              1 Month Before Due Date - Sent
                                            </Typography>
                                          </Box>
                                        )}
                                        {!filing.reminderOneMonthSent && 
                                        !filing.reminderTwoWeeksSent && 
                                        !filing.reminderOneWeekSent && 
                                        !filing.reminderOneDayAfterPeriodEnd && 
                                        !filing.reminderTwoWeeksAfterPeriodEnd && 
                                        !filing.reminderOneMonthBeforeDue && (
                                          <Typography variant="caption" sx={{ color: '#6B7280', fontStyle: 'italic' }}>
                                            No emails sent yet
                                          </Typography>
                                        )}
                                      </Box>
                                    </Box>
                                      {history.length === 0 ? (
                                        <Typography variant="body2" sx={{ color: '#6B7280', py: 2 }}>
                                          No status changes recorded yet
                                        </Typography>
                                      ) : (
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                          {history.map((h: any, idx: number) => (
                                            <Box
                                              key={h.id}
                                              sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                p: 2,
                                                backgroundColor: '#FFFFFF',
                                                borderRadius: 1,
                                                border: '1px solid #E5E7EB',
                                              }}
                                            >
                                              <Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                                  <Chip
                                                    label={h.status}
                                                    size="small"
                                                    sx={{
                                                      ...getStatusColor(h.status),
                                                      fontWeight: 600,
                                                      fontSize: '0.75rem',
                                                    }}
                                                  />
                                                  {h.createdBy && (
                                                    <Typography variant="caption" sx={{ color: '#6B7280' }}>
                                                      marked by {h.createdBy}
                                                    </Typography>
                                                  )}
                                                </Box>
                                                {h.notes && (
                                                  <Typography variant="body2" sx={{ color: '#374151', mt: 0.5 }}>
                                                    {h.notes}
                                                  </Typography>
                                                )}
                                              </Box>
                                              <Box sx={{ textAlign: 'right' }}>
                                                <Typography variant="caption" sx={{ color: '#6B7280', display: 'block', fontWeight: 600, mb: 0.5 }}>
                                                  {h.status === FilingStatus.FILED ? 'Filed Date' : 'Status Date'}
                                                </Typography>
                                                <Typography variant="body2" sx={{ color: '#374151', display: 'block', fontWeight: 600 }}>
                                                  {new Date(h.statusDate).toLocaleDateString('en-US', {
                                                    month: 'short',
                                                    day: 'numeric',
                                                    year: 'numeric'
                                                  })}
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: '#9CA3AF', fontSize: '0.7rem', mt: 0.5, display: 'block' }}>
                                                  Recorded: {new Date(h.createdAt).toLocaleString('en-US', {
                                                    month: 'short',
                                                    day: 'numeric',
                                                    hour: '2-digit',
                                                    minute: '2-digit'
                                                  })}
                                                </Typography>
                                              </Box>
                                            </Box>
                                          ))}
                                        </Box>
                                      )}
                                    </Box>
                                  </TableCell>
                                </TableRow>
                              )}
                            </React.Fragment>
                          );
                        })}
                
              {filings.length === 0 && (
                <TableRow>
                  <TableCell colSpan={canModify ? 7 : 6} align="center" sx={{ py: 6 }}>
                    <Typography variant="body2" sx={{ color: '#6B7280' }}>
                      No filing records yet
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
      {/* Mute Email Notifications Dialog */}
      {canModify && (
        <Dialog open={quickSettingsOpen} onClose={handleCloseQuickSettings} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1.5rem', color: '#1a1a1a', pb: 2 }}>
          Email Notifications
        </DialogTitle>
        <DialogContent>
          {client && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ color: '#374151', fontWeight: 600, mb: 3 }}>
                {client.clientName}
              </Typography>

              <Divider sx={{ mb: 3 }} />

              {/* Mute Notifications Toggle */}
              <Box sx={{ p: 2, backgroundColor: '#F9FAFB', borderRadius: 1, border: '1px solid #E5E7EB' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={muteNotifications}
                      onChange={(e) => setMuteNotifications(e.target.checked)}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: '#667eea',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: '#667eea',
                        },
                      }}
                    />
                  }
                  label="Mute Email Notifications"
                />
                <Typography variant="body2" sx={{ color: '#6B7280', fontSize: '0.875rem', mt: 1, ml: 5 }}>
                  When enabled, this client will not receive any automated email reminders
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button
            onClick={handleCloseQuickSettings}
            sx={{ textTransform: 'none', color: '#6B7280', fontWeight: 600, px: 3 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveQuickSettings}
            sx={{
              textTransform: 'none',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: '#FFFFFF',
              fontWeight: 600,
              px: 4,
              '&:hover': { background: 'linear-gradient(135deg, #5568d3 0%, #63408a 100%)' },
            }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
      )}
{/* Edit Filing Dialog */}
      <Dialog 
        open={editDialogOpen} 
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1.5rem', color: '#1a1a1a' }}>
          Edit Filing Dates
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Period Start Date"
              type="date"
              value={editFormData.periodStartDate}
              onChange={(e) => setEditFormData({
                ...editFormData,
                periodStartDate: e.target.value
              })}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            
            <TextField
              label="Period End Date"
              type="date"
              value={editFormData.periodEndDate}
              onChange={(e) => setEditFormData({
                ...editFormData,
                periodEndDate: e.target.value
              })}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            
            <TextField
              label="Due Date"
              type="date"
              value={editFormData.dueDate}
              onChange={(e) => setEditFormData({
                ...editFormData,
                dueDate: e.target.value
              })}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />

            <Alert severity="info">
              This will only update this individual filing record. 
              The client's main period dates will not be changed.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={() => setEditDialogOpen(false)}
            startIcon={<CancelIcon />}
            sx={{ textTransform: 'none', color: '#6B7280', fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSaveEditedFiling}
            variant="contained"
            startIcon={<SaveIcon />}
            sx={{
              textTransform: 'none',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: '#FFFFFF',
              fontWeight: 600,
              px: 4,
              '&:hover': { background: 'linear-gradient(135deg, #5568d3 0%, #63408a 100%)' },
            }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
export default ClientDetail;
